import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/Dialog';

interface ConnectDisclosureDialogProps {
  open: boolean;
  /** The service's catalog name. */
  serviceName: string;
  /** The catalog's description: Google's in-product disclosure, shown verbatim. */
  description: string;
  /**
   * Starts the Connect. Called in Continue's own click handler, so the sign-in tab it opens is
   * opened by the click (CC D16).
   */
  onContinue: () => void;
  /** Cancel, Escape or a click outside: the dialog closes, and nothing else happens. */
  onClose: () => void;
}

/**
 * Google's disclosure in a dialog of its own, immediately before Google's consent (GS §9, CC D15):
 * a Google service's Connect, Try again, Reconnect and "open sign-in again" open it, and only its
 * Continue opens the sign-in.
 */
export function ConnectDisclosureDialog({
  open,
  serviceName,
  description,
  onContinue,
  onClose,
}: ConnectDisclosureDialogProps) {
  const { t } = useTranslation('integrations');
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent showClose={false}>
        <DialogHeader>
          <DialogTitle>
            {t('disclosure.title', 'Before you connect {{service}}', { service: serviceName })}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="whitespace-pre-line text-foreground">
          {description}
        </DialogDescription>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
          >
            {t('disclosure.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t('disclosure.continue', 'Continue')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
